import { Component, ElementRef, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { ILocationService } from 'angular';
import { find, reduce, size } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  ICollectionToMerge,
  StudentCollectionsMergeService,
} from 'Src/ng2/shared/services/student-collections-merge/student-collections-merge.service';
import { UtilitiesService } from 'Src/ng2/shared/services/utilities/utilities.service';
import { IFlag } from 'Src/ng2/shared/typings/interfaces/flags.interface';
import { IMilestone } from 'Src/ng2/shared/typings/interfaces/milestones.interface';
import { IStudentSupport } from 'Src/ng2/shared/typings/interfaces/student-support.interface';
import { IStudent } from 'Src/ng2/shared/typings/interfaces/student.interface';
import { IStudentPath } from 'Src/ng2/shared/typings/interfaces/studentPaths.interface';
import { LIST_TYPE } from '../../shared/constants/list-view/list-view.constant';
import { NAV_DATA_TYPE } from '../../shared/constants/list-view/nav-data-type';
import { UpdateMadlib } from '../../store';
import { CourseRollupGrouping } from './../../shared/services/mad-lib-services/grouping-functions/course-rollup/course-rollup';
import { SubjectRollupGrouping } from './../../shared/services/mad-lib-services/grouping-functions/subject-rollup/subject-rollup';
import { TeacherRollupGrouping } from './../../shared/services/mad-lib-services/grouping-functions/teacher-rollup/teacher-rollup';
import { MadLibGroupings } from './../../shared/services/mad-lib-services/mad-lib-groupings';
import { IMadlibOptions, MadLibHelpers } from './../../shared/services/mad-lib-services/mad-lib-helpers';
import { MadLibSorting } from './../../shared/services/mad-lib-services/mad-lib-sorting';
import { LoadDashboard, ResetMadlib, SetCalculatedGraph } from './../../store/actions/dashboard-actions';
import { DIMENSION_1, MID_LEVEL_GROUPINGS } from './graph-detail-madlib.constant';
import { GRAPH_COLUMNS } from './graph-detail.constant';

interface IMadLibOption {
  madLib: string;
  helperText?: string;
  options: any[];
}

interface IMadLibModelDimension {
  dimension: string | number;
  filterStudent: Function;
  human: string;
  isActive: boolean;
  key: string;
  order: number;
}

interface IMadLibModel {
  [dimension: string]: IMadLibModelDimension;
}

/* istanbul ignore next */
@Component({
  selector: 'graph-detail',
  template: require('./graph-detail.component.html'),
  styles: [require('./graph-detail.scss')],
})
export class GraphDetailComponent implements OnInit, OnDestroy {
  public schoolId: string;
  public columns: any[];
  public schoolType: string;
  public filteredMadlib: IMadLibOption[];
  public madLibModel: any;
  public filteredStudents$;
  public groupings$;
  public madlibOptions;
  public studentsWithFlags$;
  public hasMadlibs: boolean;
  public hasMadlibGrouping: boolean;
  public showMidLevelDescription: boolean;
  public isInitialLoad: boolean;
  public sortState;
  public setPanelShadowStyle: boolean = false;

  public destroy$: Subject<boolean> = new Subject<boolean>();

  @Input() madlibOptions$;
  @Input() students$;
  @Input() studentSupports$;
  @Input() school;
  @Input() user;
  @Input() flags$;
  @Input() milestones$;
  @Input() studentPaths$;
  @Input() madlibState: string;
  @Input() graphData;
  @ViewChild('dashContainer') dashContainer: ElementRef;

  constructor(
    private madLibGroupings: MadLibGroupings,
    private courseRollupGrouping: CourseRollupGrouping,
    private subjectRollupGrouping: SubjectRollupGrouping,
    private teacherRollupGrouping: TeacherRollupGrouping,
    private madLibHelpers: MadLibHelpers,
    private madLibSorting: MadLibSorting,
    private store: Store<any>,
    @Inject('$state') private $state,
    @Inject('$location') public $location: ILocationService,
    private utilitiesService: UtilitiesService,
    private studentCollectionsMerger: StudentCollectionsMergeService,
  ) {}

  ngOnInit(): void {
    this.schoolType = this.school.schoolType;
    this.schoolId = this.school._id;
    this.studentsWithFlags$ = this.mergeStudentsAndCollections(
      this.students$,
      this.flags$,
      this.studentSupports$,
      this.milestones$,
      this.studentPaths$,
    );
    this.hasMadlibs = this.graphData.hasMadlibs;
    this.hasMadlibGrouping = this.graphData.hasMadlibGrouping;
    this.showMidLevelDescription = !this.hasMadlibs && !this.hasMadlibGrouping;
    this.sortState = { sortedColumn: null, sortOrder: null, dataType: null };

    // TODO: All logic below should live in the `mad-lib-state-resolver`. refactor like lists(JJ)
    // get dimension options based on user role / schoolType / selected graph
    const dimension1 = { ...DIMENSION_1, ...{ options: DIMENSION_1.options[this.schoolType] } };
    // we should move away from `groupings` and just continue incrementing the dim#
    // dim1... dim2.... dim3.... dim4... etc  It will be more flexible(JJ)
    const groupings = {
      ...MID_LEVEL_GROUPINGS,
      ...{
        options: MID_LEVEL_GROUPINGS.options[this.schoolType][this.graphData.key],
      },
    };
    // abstract valid dim1 options
    this.madlibOptions$
      .pipe(takeUntil(this.destroy$))
      .subscribe((options: IMadlibOptions) => (this.madlibOptions = options));
    // create options with school, students & valid madlib options
    if (this.schoolType === '6 to 12' || this.schoolType === 'K to 8') {
      this.students$.pipe(takeUntil(this.destroy$)).subscribe(students => {
        this.madlibOptions = this.madLibHelpers.createHybridMadlibOptions(
          this.schoolType,
          students,
          this.madlibOptions,
        );
      });
    }
    // all options
    const madLib: IMadLibOption[] = [dimension1, groupings];
    // filtered options
    this.filteredMadlib = this.madLibHelpers.filterMadLib(madLib, this.madlibOptions, this.school, 'MANAGER');
    // kick it off
    this.run();
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  run(newDimensionOption?, lib?, sort?): void {
    this.isInitialLoad = !newDimensionOption && !lib && !sort;

    if (newDimensionOption) this.madLibModel = this.updateMadLibModel(this.madLibModel, newDimensionOption, lib);
    else if (this.isInitialLoad) this.madLibModel = this.initializeMadlibModel(this.filteredMadlib);
    else this.madLibModel = this.madLibModel;

    // get the columns based on the madlibModel
    this.columns = this.madLibHelpers.getListColumns(this.madLibModel);

    const validColumns = this.getValidColumns(this.columns);
    const { sortKey, dataType, orderBy } = this.madLibSorting.getSort(sort, newDimensionOption, validColumns);

    // update url based on the madLib selection
    this.updateUrl(this.madLibModel);

    // keep tabs on the selected madlib options within the store
    this.store.dispatch(new UpdateMadlib(this.madLibModel));

    // graphQuery logic -- allows us to load the appropriate graph based on the madlib dim1 selection
    let graphQuery: string;
    const { Dimension1: dim1Selection } = this.madLibModel;
    if (dim1Selection.isCalculatedGraphQuery) graphQuery = dim1Selection.getGraphQuery(this.madlibOptions);
    else graphQuery = dim1Selection.getGraphQuery();
    // graphQuery not needed for graphs with no mid-level madlibs (JE)
    if (!this.hasMadlibs) graphQuery = null;

    // filter the students based on the current dimension1 selection
    this.filteredStudents$ = this.madLibHelpers.getFilteredStudents(
      this.studentsWithFlags$,
      this.madLibModel.Dimension1,
    );

    // dispatch graph action
    if (!sort) {
      // don't re-render the graph if sorting on a column (JE)
      const graphPayload = {
        schoolId: this.schoolId,
        graphName: this.graphData.key,
        graphQuery,
      };

      if (!this.graphData.isCalculatedState) {
        this.store.dispatch(new LoadDashboard(graphPayload));
      } else {
        const graphState = this.graphData.calculateGraphState(this.filteredStudents$);
        const payload = { graphState, graphName: this.graphData.key };
        this.store.dispatch(new SetCalculatedGraph(payload));
      }
    }

    // generate grouping for list component
    const currentGroupingKey = this.madLibModel.Groupings.key;
    let grouping$ = this.groupStudents(currentGroupingKey);
    if (sort) {
      this.sortState = { sortedColumn: sortKey, sortOrder: orderBy, dataType };
      grouping$ = this._sortGroupings(grouping$, sort, this.sortState, this.isInitialLoad);
    }
    this.groupings$ = grouping$;
  }

  // TODO: move to service
  /* istanbul ignore next */
  mergeStudentsAndCollections(
    students$: Observable<IStudent[]>,
    flags$: Observable<IFlag[]>,
    studentSupports$: Observable<IStudentSupport[]>,
    milestones$: Observable<IMilestone[]>,
    studentPaths$: Observable<IStudentPath[]>,
  ) {
    return combineLatest(
      students$,
      flags$,
      studentSupports$,
      milestones$,
      studentPaths$,
      (
        students: IStudent[],
        flags: IFlag[],
        studentSupports: IStudentSupport[],
        milestones: IMilestone[],
        filteredStudentPaths: IStudentPath[],
      ) => {
        const studentsWithMergedCollections = this.studentCollectionsMerger.mergeCollectionsToStudents({
          students,
          collections: [
            {
              collection: flags,
              keyToAddToStudent: 'flags',
              pathToStudentId: `studentId`,
            } as ICollectionToMerge,
            {
              collection: studentSupports,
              keyToAddToStudent: 'activeStudentSupports',
              pathToStudentId: 'student.studentId',
            } as ICollectionToMerge,
            {
              collection: milestones,
              keyToAddToStudent: 'milestones',
              pathToStudentId: `studentId`,
            } as ICollectionToMerge,
            {
              collection: filteredStudentPaths,
              keyToAddToStudent: 'studentPaths',
              pathToStudentId: `studentId`,
            } as ICollectionToMerge,
          ],
        });

        return studentsWithMergedCollections;
      },
    );
  }

  goToDashboard(): void {
    this.store.dispatch(new ResetMadlib());
    this.goToState('main.school.lists.tiles');
  }

  goToState(state: string): void {
    this.$state.go(state, { graphName: this.graphData.key }, { location: 'replace' });
  }

  getGroupingsParams(groupingKey: string) {
    return {
      filteredStudents$: this.filteredStudents$,
      madLibModel: this.madLibModel,
      columns: this.columns,
      listType: LIST_TYPE.GRAPH_DETAIL,
      navDataType: groupingKey,
      navRoute: 'main.school.lists.graph-list',
      navData: { key: 'dependencies', index: 0 },
      flagType: this.graphData.type,
      schoolType: this.schoolType,
    };
  }

  groupStudents(groupingSelection: string): Observable<any> {
    const { key } = this.graphData;
    switch (groupingSelection) {
      case 'CLASS_OF':
        const classOfGroupingParams = this.getGroupingsParams(NAV_DATA_TYPE.CLASS_OF);
        return this.madLibGroupings.getClassOfGroupings(classOfGroupingParams);
      case 'GRADE':
        const gradeGroupingParams = this.getGroupingsParams(NAV_DATA_TYPE.GRADE);
        return this.madLibGroupings.getGradeGroupings(gradeGroupingParams);
      case 'TEACHER':
        const teacherGroupingParams = this.getGroupingsParams(NAV_DATA_TYPE.TEACHER);
        return this.teacherRollupGrouping.getGrouping(this.filteredStudents$, teacherGroupingParams);
      case 'POINT_PERSON':
        const pointPersonGroupingParams = this.getGroupingsParams(NAV_DATA_TYPE.POINT_PERSON);
        return this.madLibGroupings.getPointPersonGroupings(pointPersonGroupingParams);
      case 'GRAD_PLAN':
        const gradPlanGroupingParams = this.getGroupingsParams(NAV_DATA_TYPE.GRAD_PLAN);
        return this.madLibGroupings.getGradPlanGroupings(gradPlanGroupingParams);
      case 'SUBJECT':
        const subjectGroupingParams = this.getGroupingsParams(NAV_DATA_TYPE.SUBJECT);
        return this.subjectRollupGrouping.getGrouping(this.filteredStudents$, subjectGroupingParams);
      case 'COURSE':
        const courseGroupingParams = this.getGroupingsParams(NAV_DATA_TYPE.COURSE);
        return this.courseRollupGrouping.getGrouping(this.filteredStudents$, courseGroupingParams);
      default:
        return;
    }
  }

  initializeMadlibModel(madlib: IMadLibOption[]) {
    const currentUrlDimensions = this.$location.search();
    const isMadlibStateInStore: boolean = !!size(this.madlibState);
    const urlContainsMadlibOptions: boolean = !!size(currentUrlDimensions);

    if (isMadlibStateInStore) return this.madlibState;
    else if (!urlContainsMadlibOptions) return this.initializeMadLibModelToDefaults(madlib);
    else return this.initializeMadLibModelFromUrl(currentUrlDimensions, madlib);
  }

  updateUrl(madlibModel: IMadLibModel) {
    const madlibParams = reduce(
      madlibModel,
      (urlParams, v: any, k) => {
        urlParams[k] = v.key;
        return urlParams;
      },
      {},
    );
    this.$state.go('.', madlibParams, { notify: false });
  }

  updateMadLibModel(madLibModel: IMadLibModel, newDimension: string, lib: IMadLibOption) {
    const dimension = lib.madLib;
    const updatedMadLibModel: any = { ...madLibModel, ...{ [dimension]: newDimension } };
    updatedMadLibModel.Groupings.columns = GRAPH_COLUMNS[this.graphData.key];
    this.store.dispatch(new UpdateMadlib(updatedMadLibModel));
    return updatedMadLibModel;
  }

  initializeMadLibModelToDefaults(madlib: IMadLibOption[]) {
    const [dimension1, groupings] = madlib;
    const defaultDimension1 = dimension1.options[0];
    const defaultGrouping = groupings.options[0];
    return {
      Dimension1: defaultDimension1,
      Groupings: { ...defaultGrouping, ...{ columns: GRAPH_COLUMNS[this.graphData.key] } },
    };
  }

  initializeMadLibModelFromUrl(currentUrlDimensions: { Dimension1: object; Groupings: object }, madlib) {
    const [dimension1, groupings] = madlib;
    const { Dimension1, Groupings } = currentUrlDimensions;
    const dimension1Options = dimension1.options;
    const groupingsOptions = groupings.options;

    let selection1 = find(dimension1Options, { key: Dimension1 });

    if (!selection1) {
      selection1 = reduce(
        dimension1Options,
        (result, { children }, k) => {
          if (!children) return result; // not all dims have children
          const selection = find(children, (child: any) => {
            // intentionally coercing the value here. grades are currently numbers and strings
            // we should updated the dimension1 constants to be the same type ie: strings(JJ)
            /* tslint:disable-next-line */
            return child.key == Dimension1;
          });
          if (selection) result = selection;
          return result;
        },
        null,
      );
    }

    // not nested, top level find
    let selection2 = find(groupingsOptions, { key: Groupings });

    return {
      Dimension1: selection1,
      Groupings: { ...selection2, ...{ columns: GRAPH_COLUMNS[this.graphData.key] } },
    };
  }

  _sortGroupings(grouping$: Observable<any>, { key, dataType, orderBy }, sortState, isInitialLoad) {
    const grouping = this.madLibHelpers.getSortedStudentGrouping(
      grouping$,
      key,
      dataType,
      orderBy,
      sortState,
      isInitialLoad,
    );
    return grouping;
  }

  getValidColumns(columns: any[]): string[] {
    return columns.map(({ key }) => key);
  }

  onListScroll() {
    this.setPanelShadowStyle = this.utilitiesService.showListPanelShadow(this.dashContainer.nativeElement.scrollTop);
  }
}
